// authedFetch wraps window.fetch, attaching the current Firebase ID token when
// available. The token ref is updated by SessionProvider via onIdTokenChanged,
// so we always send the freshest token without passing it explicitly.

let currentToken: string | null = null
let currentBaseUrl: string | null = null

export function setAuthToken(token: string | null): void {
  currentToken = token
}

export function setApiBaseUrl(baseUrl: string): void {
  currentBaseUrl = baseUrl.replace(/\/$/, '')
}

export interface ApiErrorShape {
  code: string
  message: string
  details?: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.status = status
    this.code = code
    if (details !== undefined) this.details = details
  }
}

export interface AuthedFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  skipAuth?: boolean
}

export async function authedFetch<T>(
  path: string,
  options: AuthedFetchOptions = {},
): Promise<T> {
  if (!currentBaseUrl) {
    throw new Error('API base URL not initialized. Call setApiBaseUrl first.')
  }

  const headers = new Headers(options.headers ?? {})
  headers.set('Accept', 'application/json')

  const { body, skipAuth, ...rest } = options

  let payload: BodyInit | undefined
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json')
    payload = JSON.stringify(body)
  }

  if (!skipAuth && currentToken) {
    headers.set('Authorization', `Bearer ${currentToken}`)
  }

  const url = path.startsWith('http')
    ? path
    : `${currentBaseUrl}${path.startsWith('/') ? path : `/${path}`}`

  let response: Response
  try {
    response = await fetch(url, { ...rest, headers, body: payload })
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No pudimos conectarnos al servidor.')
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const data: unknown = text ? safeParse(text) : null

  if (!response.ok) {
    const err = extractError(data)
    throw new ApiError(
      response.status,
      err.code,
      err.message,
      err.details,
    )
  }

  return data as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function extractError(data: unknown): ApiErrorShape {
  if (
    data &&
    typeof data === 'object' &&
    'error' in data &&
    data.error &&
    typeof data.error === 'object'
  ) {
    const err = data.error as Record<string, unknown>
    return {
      code: typeof err.code === 'string' ? err.code : 'UNKNOWN',
      message:
        typeof err.message === 'string'
          ? err.message
          : 'Ocurrio un error inesperado.',
      details: err.details,
    }
  }
  return { code: 'UNKNOWN', message: 'Ocurrio un error inesperado.' }
}
